import { Injectable } from '@angular/core'
import { GbDashboardDialogService } from '@galabid/gb-dashboard/shared/utils'
import { GbErrorMessageService } from '@galabid/shared/utils/helpers'
import { GbNotificationService } from '@galabid/shared/utils/notification'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { saveAs } from 'file-saver'
import { GbLoaderService } from 'libs/shared/utils/loader/src/lib/services/gb-loader.service'
import { catchError, exhaustMap, finalize, map, of, switchMap, tap } from 'rxjs'
import { GbStaffReportsDownloadDialogComponent } from 'libs/gb-dashboard/staff/feature-shell/src/lib/gb-staff-reports-download-dialog/gb-staff-reports-download-dialog.component'
import { StaffApiService } from './staff-api.service'
import * as StaffActions from './staff.actions'
import { STAFF_LOADER } from './staff.models'

@Injectable()
export class StaffEffects {
  exportStaffReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StaffActions.exportStaffReport),
      switchMap((action) => {
        return this.staffApiService.exportStaffReport(action.staffReportType, action.startDate, action.endDate).pipe(
          map(() => StaffActions.exportStaffReportSuccess()),
          catchError((error) => {
            return of(StaffActions.exportStaffReportFailure({ error }))
          }),
        )
      }),
    ),
  )

  exportVoucherReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StaffActions.exportVoucherReport),
      switchMap((action) => {
        return this.staffApiService.exportVoucherReport(action.startDate, action.endDate).pipe(
          map((blob) => {
            saveAs(blob, `voucher-report-${action.startDate}-${action.endDate}`)
            return StaffActions.downloadStaffReportSuccess()
          }),
          catchError((error) => {
            return of(StaffActions.exportStaffReportFailure({ error }))
          }),
        )
      }),
    ),
  )

  openStaffReportsDownloadDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(StaffActions.openDownloadReportDialog),
        exhaustMap((action) =>
          this.gbDashboardDialogService.getDialogObs(GbStaffReportsDownloadDialogComponent, {
            disableClose: true,
            data: {
              staffReportType: action.staffReportType,
              dialogTitle: action.dialogTitle,
            },
          }),
        ),
      ),
    { dispatch: false },
  )

  closeStaffReportsDownloadDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(StaffActions.exportStaffReportSuccess),
        map(() => this.gbDashboardDialogService.closeDialog(GbStaffReportsDownloadDialogComponent)),
      ),
    { dispatch: false },
  )

  showErrorToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(StaffActions.exportStaffReportFailure, StaffActions.createInvalidationFailure),
        map((action) => {
          const errorMessage = this.gbErrorMessageService.getErrorMessage(action.error['error'])
          if (errorMessage && typeof errorMessage === 'string') this.gbNotificationService.error(errorMessage)
          else {
            this.gbNotificationService.error('GENERIC.ERROR')
          }
        }),
      ),
    { dispatch: false },
  )

  downloadStaffReportSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(StaffActions.downloadStaffReportSuccess),
        map(() => this.gbNotificationService.success('STAFF.DOWNLOAD_REPORT_SUCCESS')),
      ),
    { dispatch: false },
  )

  showSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(StaffActions.exportStaffReportSuccess),
        map(() => this.gbNotificationService.success('STAFF.EXPORT_REPORT_SUCCESS')),
      ),
    { dispatch: false },
  )

  createInvalidation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StaffActions.createInvalidation),
      exhaustMap((action) =>
        this.gbDashboardDialogService.filterByConfirmDialog(
          {
            title: 'STAFF.AWS_TOOLS.CONFIRM.TITLE',
            body: 'STAFF.AWS_TOOLS.CONFIRM.DESCRIPTION',
            confirmButtonText: 'CONFIRM',
            cancelButtonText: 'CANCEL',
          },
          action,
        ),
      ),
      tap(() => this.gbLoaderService.showLoaderDialog(STAFF_LOADER.createInvalidation.id)),
      switchMap((action) => {
        return this.staffApiService.createInvalidation().pipe(
          map((response) => StaffActions.createInvalidationSuccess()),
          catchError((error) => {
            return of(StaffActions.createInvalidationFailure({ error }))
          }),
          finalize(() => this.gbLoaderService.hideLoaderDialog(STAFF_LOADER.createInvalidation.id)),
        )
      }),
    ),
  )

  showCloudFrontSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(StaffActions.createInvalidationSuccess),
        map(() => this.gbNotificationService.success('STAFF.AWS_TOOLS.SUCCESS')),
      ),
    { dispatch: false },
  )

  constructor(
    private staffApiService: StaffApiService,
    private readonly actions$: Actions,
    private gbErrorMessageService: GbErrorMessageService,
    private gbNotificationService: GbNotificationService,
    private gbDashboardDialogService: GbDashboardDialogService,
    private gbLoaderService: GbLoaderService,
  ) {}
}
